<template>
    <v-container fluid>
        <div v-if="!dataLoading">
            <v-row>
                <v-col>
                    <h4 class="text-h4">New Purchase Order</h4>
                    <p class="text-subtitle-1">Purchase Order Creation Form</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn
                        @click="exchangeRateIndexShown = true"
                        block
                        depressed
                        >
                        <v-icon left>mdi-chart-areaspline-variant</v-icon>
                        currency exchange
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <PurchaseOrderForm
                        ref="orderForm"
                        v-model="data"
                        :exchange-rate-index-shown.sync="exchangeRateIndexShown"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn @click="handleSave" block color="primary" :loading="loading">
                        <v-icon left>mdi-content-save</v-icon>
                        save
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn block depressed  @click="handleList" v-if="hasPermission(perm.purchase_order.index)">
                        cancel
                    </v-btn>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
        </div>
        <PAlert v-model="alert.value" v-bind="alert"/>
    </v-container>
</template>

<script>
import permissions from './../../data/permissions';
import { mapActions, mapGetters } from 'vuex';
import PurchaseOrderForm from "./../../components/purchase/PurchaseOrderForm.vue"
import PAlert from './../../components/common/PAlert';
import { v4 as uuid } from 'uuid';
import { randomSix } from '../../utils/toolkit';
import moment from 'moment';

export default {
    name: "PurchaseOrderCreateView",
    components: {
        PurchaseOrderForm,
        PAlert,
    },
    data() {
        return {
            alert: {
                value: false,
                title: "Alert",
                content: "Are you sure?",
                callback: null
            },
            loading: false,
            dataLoading: false,
            data : null,
            perm: permissions,
            idempotentKey: null,
            exchangeRateIndexShown: false
        }
    },
    computed: {
        ...mapGetters({
            hasPermission: "authentication/hasPermission"
        })
    },
    methods: {
        ...mapActions({
            create: "purchase/order/create",
            findRequisitionByID: "purchase/requisition/findByID",
            snackbar: "ui/snackbar/show",
            pushItem: "purchase/item/push",
            pushWarehouse: "purchase/warehouse/push",
            findSupplier: "purchase/supplier/find",
            pushSupplier: "purchase/supplier/push"
        }),
        handleSave() {
            this.alert = {...this.alert, content: "Are you sure you want to save?", value: true, callback: this.save }
        },
        save() {
            if(!this.$refs.orderForm.validate()) return ;
            this.loading = true;
            this.create({...this.data, idempotentKey: this.idempotentKey}).then(response => {
                this.$router.push({name: "PurchaseOrderDisplay", params: {id: response.data.id}})
                this.snackbar({text: "Purcahse Order Created", color: "success"})
            }).catch(error => {
                let message = error.response ? error.response.data.message : error;
                this.snackbar({text: message, color: "error"})
            }).finally(() => {
                this.loading = false
            });
        },
        handleList() {
            this.$router.push({name: "PurchaseOrderList"})
        },
        fetchRequisition(requisitionId) {
            this.dataLoading = true;
            this.findRequisitionByID(requisitionId)
                .then(response => {
                    this.data = this.mapRequisitionToOrder(response.data);
                })
                .catch(error => {
                    let message = error.response ? error.response.data.message : error;
                    this.snackbar({text: message, color: "error"})
                })
                .finally(() => {
                    this.dataLoading = false
                });
        },
        mapRequisitionToOrder(requisition) {
            this.pushWarehouse(requisition.warehouse);
            return {
                date: moment().format("YYYY-MM-DD"),
                warehouse_id: requisition.warehouse.id,
                receiving_point: requisition.receiving_point,
                requisition_id: requisition.id,
                requisition_code: requisition.code,
                lines: requisition.lines.map(line => {
                    this.pushItem(line.item);
                    return {
                        item_id: line.item.id,
                        measure_unit_id: line.measure_unit.id,
                        quantity: line.quantity - line.ordered,
                        key: randomSix()
                    }  
                })
            }
        }
    },
    mounted() {
        if(this.$route.query.requisition_id) {
            this.fetchRequisition(this.$route.query.requisition_id);
            return;
        }
        if(this.$route.query.supplier_id) {
            this.data = {}
            this.data.supplier_id = Number(this.$route.query.supplier_id)
            this.dataLoading = true;
            this.findSupplier(this.$route.query.supplier_id).then(response => {
                this.pushSupplier(response.data)
            }).finally(() => {
                this.dataLoading = false;
                this.$nextTick(() => {
                    this.$refs.orderForm.addLine();
                })
            })
            return;
        }
        this.$refs.orderForm.addLine();
    },
    created() {
        this.idempotentKey = uuid();
    }
}
</script>

<style>

</style>